import React from 'react';
import { Spin } from 'antd';
import { Component } from '../../../common/componentUtils';
import { PDFViewer } from '../../../components/PDFViewer';
import { Variable } from '../../../data/interfaces/DocumentTemplate';
import { ComponentEditor } from '../ComponentEditor';
import {
	DocumentPanelLoader,
	DocumentTitle,
	FileName,
	FileNameTooltip,
	NavigationPanel,
	paddingTop,
	StyledDocumentsPanel,
} from './DocumentsPanel.styled';

interface DocumentsPanelProps {
	documentName: string;
	pdf: any;
	onPageSelect(pageNumber: number): void;
	selectedComponents: Component[];
	onClearSelectedComponent: () => void;
	onComponentUpdate: (components: Component[], operation: string) => void;
	checkboxGroupComponents: Component[];
	addCheckbox: any;
	removeCheckbox: any;
	onComponentSelect: (components: Component[]) => void;
	variables: Variable[] | undefined;
	containerHeight: number;
}

export const DocumentsPanel = ({
	documentName,
	pdf,
	onPageSelect: onSelect,
	selectedComponents: componentsToEdit,
	onClearSelectedComponent,
	onComponentUpdate: handleComponentUpdate,
	checkboxGroupComponents,
	addCheckbox,
	removeCheckbox,
	onComponentSelect,
	variables,
	containerHeight,
}: DocumentsPanelProps) => {
	// sets up a keydown event listener that listens for the delete key press
	React.useEffect(() => {
		function handleKeyDown(event) {
			if (componentsToEdit && event.key === 'Escape') {
				// This is for handling the text annotation
				if (event.target.nodeName === 'TEXTAREA') {
					event.target.readOnly = true;
					event.target.autofocus = false;
				}
				onClearSelectedComponent();
				return;
			}
			if (event.target.nodeName === 'INPUT' || event.target.nodeName === 'TEXTAREA') {
				return;
			}
			if (componentsToEdit[0] && (event.key === 'Backspace' || event.key === 'Delete')) {
				handleComponentUpdate(componentsToEdit, 'delete');
				onClearSelectedComponent();
			}
		}

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [componentsToEdit, handleComponentUpdate, onClearSelectedComponent]);

	const loader = (
		<DocumentPanelLoader vertical justify="center" height={containerHeight}>
			<Spin size="large" />
		</DocumentPanelLoader>
	);

	return (
		<StyledDocumentsPanel>
			{componentsToEdit?.length > 0 && (
				<ComponentEditor
					componentsToEdit={componentsToEdit}
					onClose={() => onClearSelectedComponent()}
					handleComponentUpdate={handleComponentUpdate}
					variables={variables}
					addCheckbox={addCheckbox}
					removeCheckbox={removeCheckbox}
					checkboxGroupComponents={checkboxGroupComponents}
					onComponentSelect={onComponentSelect}
				/>
			)}
			{/* Do not use conditional rendering since PDF will be re-rendered. Instead simply hide it */}
			<NavigationPanel showPanel={componentsToEdit.length === 0}>
				<DocumentTitle>
					<FileNameTooltip
						hoverTrigger={<FileName>{documentName}</FileName>}
						placement="top"
					>
						{documentName}
					</FileNameTooltip>
				</DocumentTitle>

				<PDFViewer
					pdf={pdf}
					containerHeight={containerHeight}
					onPageClick={onSelect}
					pageHeight={300}
					maxPageWidth={198}
					loader={loader}
					highlightClickedPage
					pageContainerStyle={{ padding: `${paddingTop}px 0 0` }}
					pageStyle={{ padding: 0 }}
				/>
			</NavigationPanel>
		</StyledDocumentsPanel>
	);
};
